import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { showToast } from '../../../components/Toast';
import PrintModal from '../../../components/PrintModal';
import { formatNumber } from '../../../utils/formatters';
import { API_DOCS } from './OfferteCapitolatoPage.api';
import './OfferteCapitolatoPage.css';

// -- Icone -------------------------------------------------------------------

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const PrinterIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 6 2 18 2 18 9" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" />
  </svg>
);
const SendIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);
const ConvertIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

// -- Tipi --------------------------------------------------------------------

type StatoOfferta = 'BOZZA' | 'INVIATA_CLIENTE' | 'CONVERTITA_COMMESSA';

interface SedeOption {
  id: number;
  nome: string;
}

interface CodiceIvaOption {
  id: number;
  codice: string;
  descrizione: string;
  aliquota: string;
  predefinito?: boolean;
}

interface OffertaItem {
  id: number;
  codice: string;
  stato: StatoOfferta;
  oggetto: string;
  clienteNome: string;
  sedeId: number | null;
  createdAt: string;
  createdBy: { id: number; nome: string } | null;
  sede: { id: number; nome: string } | null;
  _count: { righe: number };
  commesse: { id: number; numero: string }[];
}

// -- Helpers -----------------------------------------------------------------

const STATO_LABEL: Record<StatoOfferta, string> = {
  BOZZA: 'Bozza',
  INVIATA_CLIENTE: 'Inviata',
  CONVERTITA_COMMESSA: 'Convertita',
};

const STATO_CLS: Record<StatoOfferta, string> = {
  BOZZA: 'ofc-stato-badge--bozza',
  INVIATA_CLIENTE: 'ofc-stato-badge--inviata',
  CONVERTITA_COMMESSA: 'ofc-stato-badge--convertita',
};

// -- Modale genera commessa --------------------------------------------------

export function ModaleGeneraCommessa({
  offerta,
  societaId,
  onClose,
  onGenerata,
}: {
  offerta: OffertaItem;
  societaId: string;
  onClose: () => void;
  onGenerata: (commessaId: number, numero: string) => void;
}) {
  const [sedi, setSedi] = useState<{ id: number; nome: string }[]>([]);
  const [utenti, setUtenti] = useState<{ id: number; nome: string }[]>([]);
  const [sedeId, setSedeId] = useState('');
  const [responsabileId, setResponsabileId] = useState('');
  const [codiceIvaDefaultId, setCodiceIvaDefaultId] = useState('');
  const [codiciIva, setCodiciIva] = useState<CodiceIvaOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingDati, setLoadingDati] = useState(true);
  const [erroreDati, setErroreDati] = useState('');

  useEffect(() => {
    const carica = async () => {
      try {
        // Sedi commerciali accessibili all'utente per la società corrente
        // @api GET /api/sedi/mie?societaId={id}&commerciale=true -> {"success":"true","data":[{"id":"number","nome":"string"}]}
        const resSedi = await apiFetch<{ success: boolean; data: { id: number; nome: string }[] }>(
          `/api/sedi/mie?societaId=${societaId}&commerciale=true`
        );
        setSedi(resSedi.data || []);

        // Utenti attivi della società selezionabili come responsabile
        // @api GET /api/utenti/selezionabili?societaId={id} -> {"success":"true","data":[{"id":"number","nome":"string"}]}
        const resUtenti = await apiFetch<{ success: boolean; data: { id: number; nome: string }[] }>(
          `/api/utenti/selezionabili?societaId=${societaId}`
        );
        setUtenti(resUtenti.data || []);
      } catch (err: unknown) {
        // Un 403 o un errore di rete non deve sembrare "nessun dato"
        const msg = err instanceof Error ? err.message : 'Errore di rete';
        console.error('[OfferteCapitolatoPage][ModaleGeneraCommessa]', err);
        setErroreDati(`Impossibile caricare sedi e responsabili: ${msg}`);
        showToast('Errore nel caricamento di sedi e responsabili', 'error');
      } finally {
        setLoadingDati(false);
      }
    };
    if (societaId) carica();
  }, [societaId]);

  // Preseleziona la sede dell'offerta se è presente tra le sedi commerciali caricate
  useEffect(() => {
    if (sedi.length === 0 || sedeId) return; // già selezionata o non caricate
    if (offerta.sedeId && sedi.some(s => s.id === offerta.sedeId)) {
      setSedeId(String(offerta.sedeId));
    }
  }, [sedi, offerta.sedeId, sedeId]);

  useEffect(() => {
    if (!societaId) { setCodiciIva([]); return; }
    // @api GET /api/codici-iva?societaId={id}&attivo=true -> {"codici":[{"id":"number","codice":"string","descrizione":"string","aliquota":"string","attivo":"boolean","predefinito":"boolean"}]}
    apiFetch<{ codici: CodiceIvaOption[] }>(`/api/codici-iva?societaId=${societaId}&attivo=true`)
      .then(r => {
        const lista = r.codici || [];
        setCodiciIva(lista);
        const pre = lista.find(c => c.predefinito);
        if (pre) setCodiceIvaDefaultId(String(pre.id));
      })
      .catch(() => setCodiciIva([]));
  }, [societaId]);

  const handleGenera = async () => {
    if (!sedeId || !responsabileId) {
      showToast('Sede e responsabile sono obbligatori', 'error');
      return;
    }
    if (!codiceIvaDefaultId) {
      showToast('Codice IVA default obbligatorio', 'error');
      return;
    }
    setLoading(true);
    try {
      // @api POST /api/offerte-capitolato/:id/genera-commessa {"sedeId":"number","responsabileId":"number","codiceIvaDefaultId":"number"} -> {"success":true,"data":{"commessaId":"number","commessaNumero":"string"}}
      const res = await apiFetch<{ success: boolean; data: { commessaId: number; commessaNumero: string } }>(
        `/api/offerte-capitolato/${offerta.id}/genera-commessa`,
        {
          method: 'POST',
          body: JSON.stringify({
            sedeId: parseInt(sedeId, 10),
            responsabileId: parseInt(responsabileId, 10),
            codiceIvaDefaultId: parseInt(codiceIvaDefaultId, 10),
          }),
        }
      );
      showToast(`Commessa ${res.data.commessaNumero} generata`, 'success');
      onGenerata(res.data.commessaId, res.data.commessaNumero);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Errore nella generazione';
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ofc-modal">
      <div className="ofc-modal__box ofc-modal__box--medium">
        <div className="ofc-modal__header">
          <h2 className="ofc-modal__title">Genera Commessa</h2>
          <button className="ofc-modal__close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="ofc-modal__body">
          <div className="ofc-genera-info">
            <p className="ofc-genera-info__label">Offerta: <strong>{offerta.codice}</strong></p>
            <p className="ofc-genera-info__label">Cliente: <strong>{offerta.clienteNome}</strong></p>
            <p className="ofc-genera-info__label">Oggetto: <strong>{offerta.oggetto}</strong></p>
          </div>
          {loadingDati ? (
            <div className="bop-text-muted">Caricamento...</div>
          ) : erroreDati ? (
            <div className="bop-alert bop-alert--danger" role="alert">{erroreDati}</div>
          ) : (
            <>
              <div className="bop-form-group">
                <label className="bop-label">Sede *</label>
                {sedi.length === 0 ? (
                  <p className="bop-text-muted">Nessuna sede commerciale disponibile per questa società.</p>
                ) : (
                  <select className="bop-select" value={sedeId} onChange={e => setSedeId(e.target.value)}>
                    <option value="">— Seleziona sede —</option>
                    {sedi.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                  </select>
                )}
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Responsabile *</label>
                {utenti.length === 0 ? (
                  <p className="bop-text-muted">Nessun responsabile disponibile per questa società.</p>
                ) : (
                  <select className="bop-select" value={responsabileId} onChange={e => setResponsabileId(e.target.value)}>
                    <option value="">— Seleziona responsabile —</option>
                    {utenti.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                  </select>
                )}
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Codice IVA default *</label>
                <select
                  className="bop-select"
                  value={codiceIvaDefaultId}
                  onChange={e => setCodiceIvaDefaultId(e.target.value)}
                  disabled={codiciIva.length === 0}
                >
                  <option value="">— Seleziona IVA —</option>
                  {codiciIva.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.codice} — {c.descrizione} ({formatNumber(parseFloat(c.aliquota))}%)
                    </option>
                  ))}
                </select>
                {codiciIva.length === 0 && (
                  <small className="bop-text-muted">Nessun codice IVA attivo per questa società</small>
                )}
              </div>
            </>
          )}
        </div>
        <div className="ofc-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose} disabled={loading}>Annulla</button>
          <button className="bop-btn bop-btn-primary" onClick={handleGenera} disabled={loading || loadingDati || !!erroreDati}>
            {loading ? 'Generazione...' : 'Genera Commessa'}
          </button>
        </div>
      </div>
    </div>
  );
}

// -- Componente principale ---------------------------------------------------

export default function OfferteCapitolatoPage() {
  const navigate = useNavigate();
  const { societaId, listaSocieta, isDisabled, pronta } = useFiltroSocieta();
  const { can, isSuperadmin } = usePermesso();
  const canEdit = can('commerciale.offerte_capitolato', 'edit');
  const canDelete = can('commerciale.offerte_capitolato', 'delete');

  const [offerte, setOfferte] = useState<OffertaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [filtroStato, setFiltroStato] = useState<'' | StatoOfferta>('');
  const [filtroSedeId, setFiltroSedeId] = useState('');
  const [sediLista, setSediLista] = useState<SedeOption[]>([]);
  const [societaIdLocale, setSocietaIdLocale] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<OffertaItem | null>(null);
  const [generaCommessaOfferta, setGeneraCommessaOfferta] = useState<OffertaItem | null>(null);
  const [printTarget, setPrintTarget] = useState<{ id: number; label: string; societaId: number } | null>(null);

  const societaIdEff = societaId || societaIdLocale;

  // Carica sedi quando cambia la società
  useEffect(() => {
    if (!societaIdEff) { setSediLista([]); return; }
    // @api GET /api/sedi/mie?societaId={id}&commerciale=true -> {"success":true,"data":[{"id":"number","nome":"string"}]}
    apiFetch<{ success: boolean; data: SedeOption[] }>(`/api/sedi/mie?societaId=${societaIdEff}&commerciale=true`)
      .then(res => setSediLista(res.data || []))
      .catch(() => setSediLista([]));
  }, [societaIdEff]);

  const carica = useCallback(async () => {
    if (!pronta) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (societaIdEff) params.set('societaId', societaIdEff);
      if (filtroSedeId) params.set('sedeId', filtroSedeId);
      if (q.trim()) params.set('q', q.trim());
      if (filtroStato) params.set('stato', filtroStato);
      // @api GET /api/offerte-capitolato?societaId={id}&sedeId={sedeId}&q={q}&stato={stato} -> {"success":true,"data":[{"id":"number","codice":"string","stato":"string","oggetto":"string","clienteNome":"string","sedeId":"number","sede":{"id":"number","nome":"string"},"_count":{"righe":"number"},"commesse":[]}]}
      const res = await apiFetch<{ success: boolean; data: OffertaItem[] }>(
        `/api/offerte-capitolato?${params}`
      );
      setOfferte(res.data || []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Errore';
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [pronta, societaIdEff, q, filtroStato, filtroSedeId]);

  useEffect(() => { carica(); }, [carica]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      // @api DELETE /api/offerte-capitolato/:id -> {"success":true}
      await apiFetch(`/api/offerte-capitolato/${confirmDelete.id}`, { method: 'DELETE' });
      showToast('Offerta eliminata', 'success');
      setConfirmDelete(null);
      carica();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Errore nell\'eliminazione';
      showToast(msg, 'error');
    }
  };

  return (
    <Layout>
      <div className="ofc-page">
        {/* Header */}
        <div className="bop-page-header">
          <h1 className="bop-page-title">Offerte Capitolato</h1>
          {canEdit && pronta && (
            <button
              className="bop-btn bop-btn-primary"
              onClick={() => navigate('/commerciale/capitolati/offerte/nuovo')}
            >
              <PlusIcon /> Nuova offerta
            </button>
          )}
        </div>

        {/* Filtri */}
        <div className="bop-card ofc-filtri">
          <div className="ofc-filtri__row">
            {!isDisabled && (
              <div className="bop-form-group">
                <label className="bop-label">Società</label>
                <select
                  className="bop-select"
                  value={societaIdLocale}
                  onChange={e => setSocietaIdLocale(e.target.value)}
                >
                  {isSuperadmin && <option value="">Tutte</option>}
                  {listaSocieta.map(s => (
                    <option key={s.id} value={String(s.id)}>{s.nome}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="bop-form-group">
              <label className="bop-label">Cerca</label>
              <input
                className="bop-input"
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Codice, oggetto, cliente..."
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Stato</label>
              <select
                className="bop-select"
                value={filtroStato}
                onChange={e => setFiltroStato(e.target.value as '' | StatoOfferta)}
              >
                <option value="">Tutti</option>
                <option value="BOZZA">Bozza</option>
                <option value="INVIATA_CLIENTE">Inviata</option>
                <option value="CONVERTITA_COMMESSA">Convertita</option>
              </select>
            </div>
            {sediLista.length > 0 && (
              <div className="bop-form-group">
                <label className="bop-label">Sede</label>
                <select
                  className="bop-select"
                  value={filtroSedeId}
                  onChange={e => setFiltroSedeId(e.target.value)}
                >
                  <option value="">Tutte</option>
                  {sediLista.map(s => (
                    <option key={s.id} value={String(s.id)}>{s.nome}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="bop-form-group">
              <label className="bop-label">&nbsp;</label>
              <button className="bop-btn bop-btn-secondary" onClick={carica}>
                <SearchIcon /> Cerca
              </button>
            </div>
          </div>
        </div>

        {/* Tabella */}
        <div className="bop-card">
          {!pronta ? (
            <div className="bop-empty-state">Seleziona una società per visualizzare le offerte</div>
          ) : loading ? (
            <div className="bop-empty-state">Caricamento...</div>
          ) : offerte.length === 0 ? (
            <div className="bop-empty-state">Nessuna offerta trovata</div>
          ) : (
            <table className="bop-table bop-table--clickable">
              <thead>
                <tr>
                  <th>Codice</th>
                  <th>Stato</th>
                  <th>Cliente</th>
                  <th>Oggetto</th>
                  <th>Sede</th>
                  <th>Righe</th>
                  <th>Commessa</th>
                  <th>Creato da</th>
                  <th>Data</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {offerte.map(ofc => (
                  <tr key={ofc.id} onClick={() => navigate(`/commerciale/capitolati/offerte/${ofc.id}`)}>
                    <td><span className="ofc-codice-badge">{ofc.codice}</span></td>
                    <td>
                      <span className={`ofc-stato-badge ${STATO_CLS[ofc.stato]}`}>
                        {STATO_LABEL[ofc.stato]}
                      </span>
                    </td>
                    <td>{ofc.clienteNome}</td>
                    <td>{ofc.oggetto}</td>
                    <td>{ofc.sede?.nome || <span className="bop-text-muted">—</span>}</td>
                    <td><span className="ofc-righe-count">{ofc._count.righe}</span></td>
                    <td>
                      {ofc.commesse.length > 0
                        ? <Link className="ofc-commessa-link" to={`/commerciale/commesse/${ofc.commesse[0].id}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{ofc.commesse[0].numero}</Link>
                        : <span className="bop-text-muted">—</span>
                      }
                    </td>
                    <td>{ofc.createdBy?.nome || <span className="bop-text-muted">—</span>}</td>
                    <td>{new Date(ofc.createdAt).toLocaleDateString('it-IT')}</td>
                    <td>
                      <div className="bop-flex">
                        <button
                          className="bop-btn bop-btn-icon"
                          title="Stampa / Invia"
                          onClick={(e) => { e.stopPropagation(); setPrintTarget({
                            id: ofc.id,
                            label: `${ofc.codice} — ${ofc.oggetto}`,
                            societaId: parseInt(societaIdEff || '0', 10),
                          }); }}
                        >
                          <PrinterIcon />
                        </button>
                        {canEdit && ofc.stato === 'INVIATA_CLIENTE' && (
                          <button
                            className="bop-btn bop-btn-icon ofc-btn-genera"
                            title="Genera Commessa"
                            onClick={(e) => { e.stopPropagation(); setGeneraCommessaOfferta(ofc); }}
                          >
                            <ConvertIcon />
                          </button>
                        )}
                        {canEdit && ofc.stato === 'BOZZA' && (
                          <button
                            className="bop-btn bop-btn-icon ofc-btn-invia"
                            title="Segna come Inviata"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                // @api GET /api/offerte-capitolato/:id -> {"success":true,"data":{"id":"number","righe":[],"righeAccessorie":[],"ricarico":"string","tipoFatturazione":"string"}}
                                const dett = await apiFetch<{ success: boolean; data: any }>(
                                  `/api/offerte-capitolato/${ofc.id}`
                                );
                                const d = dett.data;
                                // @api PUT /api/offerte-capitolato/:id {"stato":"string","oggetto":"string","clienteNome":"string","ricarico":"number","tipoFatturazione":"string","righe":[],"righeAccessorie":[]} -> {"success":true}
                                await apiFetch(`/api/offerte-capitolato/${ofc.id}`, {
                                  method: 'PUT',
                                  body: JSON.stringify({
                                    ...d,
                                    stato: 'INVIATA_CLIENTE',
                                    ricarico: d.ricarico != null ? parseFloat(d.ricarico) : null,
                                    tipoFatturazione: d.tipoFatturazione || null,
                                    righe: (d.righe || []).map((r: any) => ({
                                      articoloPreconfigId: r.articoloPreconfigId,
                                      codicePrecopy: r.codicePrecopy,
                                      nomePrecopy: r.nomePrecopy,
                                      umVenditaPrecopy: r.umVenditaPrecopy,
                                      ordine: r.ordine,
                                      prezzoUnitario: parseFloat(r.prezzoUnitario),
                                      scorporo: r.scorporo != null ? parseFloat(r.scorporo) : null,
                                      descrizioneRiga: r.descrizioneRiga || null,
                                    })),
                                    righeAccessorie: (d.righeAccessorie || []).map((r: any) => ({
                                      articoloId: r.articoloId,
                                      codicePrecopy: r.codicePrecopy,
                                      nomePrecopy: r.nomePrecopy,
                                      tipologiaPrecopy: r.tipologiaPrecopy,
                                      umVenditaPrecopy: r.umVenditaPrecopy,
                                      ordine: r.ordine,
                                      prezzoUnitario: parseFloat(r.prezzoUnitario),
                                      scorporo: r.scorporo != null ? parseFloat(r.scorporo) : null,
                                      descrizioneRiga: r.descrizioneRiga || null,
                                    })),
                                  }),
                                });
                                showToast('Offerta segnata come inviata', 'success');
                                carica();
                              } catch (err: unknown) {
                                showToast(err instanceof Error ? err.message : 'Errore', 'error');
                              }
                            }}
                          >
                            <SendIcon />
                          </button>
                        )}
                        {canDelete && ofc.stato !== 'CONVERTITA_COMMESSA' && (
                          <button
                            className="bop-btn bop-btn-icon"
                            title="Elimina"
                            onClick={(e) => { e.stopPropagation(); setConfirmDelete(ofc); }}
                          >
                            <TrashIcon />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Modale genera commessa */}
        {generaCommessaOfferta && (
          <ModaleGeneraCommessa
            offerta={generaCommessaOfferta}
            societaId={societaIdEff}
            onClose={() => setGeneraCommessaOfferta(null)}
            onGenerata={(_id, _numero) => { setGeneraCommessaOfferta(null); carica(); }}
          />
        )}

        {/* Confirm delete */}
        {confirmDelete && (
          <div className="ofc-modal">
            <div className="ofc-modal__box ofc-modal__box--medium">
              <div className="ofc-modal__header">
                <h2 className="ofc-modal__title">Elimina offerta</h2>
                <button className="ofc-modal__close" onClick={() => setConfirmDelete(null)}><XIcon /></button>
              </div>
              <div className="ofc-modal__body">
                <p>
                  Sei sicuro di voler eliminare l'offerta <strong>{confirmDelete.codice} — {confirmDelete.oggetto}</strong>?
                  Questa azione non può essere annullata.
                </p>
              </div>
              <div className="ofc-modal__footer">
                <button className="bop-btn bop-btn-secondary" onClick={() => setConfirmDelete(null)}>Annulla</button>
                <button className="bop-btn bop-btn-danger" onClick={handleDelete}>Elimina</button>
              </div>
            </div>
          </div>
        )}

        {printTarget && (
          <PrintModal
            societaId={printTarget.societaId}
            entitaType="offerta_capitolato"
            entitaId={printTarget.id}
            entitaLabel={printTarget.label}
            onClose={() => setPrintTarget(null)}
          />
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
